import { interceptorMiddleware } from '../../../shared/services/interceptor.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    BusinessLevel3Request,
    BusinessLevel3Response,
    BusinessLocation,
    BusinessUserDetais,
    BusinessUserDetaisResponse,
    Level3Response,
    NewLocation,
    VenueDetailsResponse,
} from '../utils/types.businessInfo';
import { apiUrlBusinessInfo } from '../utils/apiUrl.businessInfo';
import { MetaDataResponse } from '../../../shared/types/type';
import { HttpMethods } from '../../../shared/constants/application.constant';
import { ConstNumber } from '../../../shared/constants/number.constant';
import { getChangedValues } from '../../../shared/utils/helper';

export interface BasicAddressLocation {
    location_name: string;
    address: string;
    city: string;
    state: string | null;
    zip_code: string;
}

// Define a service using a base URL and expected endpoints
export const businessInfoApi = createApi({
    reducerPath: 'businessInfo',
    baseQuery: interceptorMiddleware,
    tagTypes: ['businessInfo'],
    endpoints: (builder) => ({
        getCategories: builder.query<
            MetaDataResponse,
            { categoryType: number }
        >({
            query: ({ categoryType }) => ({
                url: `${apiUrlBusinessInfo.GET_META}?category_type=${categoryType}`,
                method: 'GET',
            }),
            providesTags: ['businessInfo'],
        }),
        getBusinessUser: builder.query<BusinessUserDetais, {}>({
            query: () => ({
                url: apiUrlBusinessInfo.BUSINESS_PROFILE,
                method: 'GET',
            }),
            transformResponse: (
                response: BusinessUserDetaisResponse
            ): BusinessUserDetais => response.data,
            providesTags: ['businessInfo'],
        }),
        updateBusinessUser: builder.mutation<
            {},
            { data: { [key: string]: string | number[] | File } }
        >({
            query: ({ data }) => ({
                url: apiUrlBusinessInfo.GET_BUSINESS_PROFILE_UPDATE,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['businessInfo'],
        }),
        getVenueDetails: builder.query<VenueDetailsResponse, {}>({
            query: () => ({
                url: apiUrlBusinessInfo.GET_VENUE_DETAILS,
                method: 'GET',
            }),
            providesTags: ['businessInfo'],
        }),
        getBusinessLevel3: builder.query<Level3Response, { id?: number }>({
            query: ({ id }) => {
                let url = apiUrlBusinessInfo.GET_BUSINESS_LEVEL_3 as string;
                if (id) {
                    url = `${apiUrlBusinessInfo.GET_BUSINESS_LEVEL_3}?user_id=${id}`;
                }
                return {
                    url,
                    method: HttpMethods.GET,
                };
            },
            providesTags: ['businessInfo'],
        }),
        updateBusinessLevel3: builder.mutation<
            BusinessLevel3Response,
            { data: BusinessLevel3Request }
        >({
            query: ({ data }) => ({
                url: apiUrlBusinessInfo.GET_BUSINESS_LEVEL_3,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['businessInfo'],
        }),
        getBusinessLocations: builder.query<BusinessLocation[], {}>({
            query: () => ({
                url: apiUrlBusinessInfo.BUSINESS_LOCATIONS,
                method: HttpMethods.GET,
            }),
            transformResponse: (res: { data: BusinessLocation[] }) => res.data,
            providesTags: ['businessInfo'],
        }),
        addNewBusinessLocation: builder.mutation<
            BusinessLocation,
            { body: NewLocation }
        >({
            query: ({ body }) => ({
                url: apiUrlBusinessInfo.BUSINESS_PROFILE,
                method: HttpMethods.POST,
                body,
            }),
        }),
        updateBusinessLocation: builder.mutation<
            any,
            {
                data: NewLocation;
                initialValues: BasicAddressLocation;
                id: number;
            }
        >({
            query: ({ data, id, initialValues }) => {
                const changedValues = getChangedValues(initialValues, data);
                return {
                    url: `${apiUrlBusinessInfo.BUSINESS_PROFILE}${id}/location/`,
                    method: HttpMethods.PATCH,
                    body: changedValues,
                };
            },
        }),
        deleteBusinessLocation: builder.mutation<
            { message: string },
            { id: number }
        >({
            query: ({ id }) => ({
                url: `${apiUrlBusinessInfo.BUSINESS_PROFILE}${id}/`,
                method: HttpMethods.DELETE,
            }),
        }),
        getBusinessLocationById: builder.query<
            BusinessUserDetais,
            { id?: number }
        >({
            query: ({ id }) => ({
                url: `${apiUrlBusinessInfo.BUSINESS_PROFILE}${id}/`,
                method: HttpMethods.GET,
            }),
            keepUnusedDataFor: ConstNumber.VALUE_1,
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetCategoriesQuery,
    useGetBusinessUserQuery,
    useUpdateBusinessUserMutation,
    useGetVenueDetailsQuery,
    useGetBusinessLevel3Query,
    useUpdateBusinessLevel3Mutation,
    useGetBusinessLocationsQuery,
    useAddNewBusinessLocationMutation,
    useUpdateBusinessLocationMutation,
    useDeleteBusinessLocationMutation,
    useGetBusinessLocationByIdQuery,
} = businessInfoApi;
